import { useState, useCallback, useEffect } from "react";
import { useSwipeable } from "react-swipeable";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { colors } from "styles/colors";
import { ImageItem } from "types/types/Types";

interface Props {
  open: boolean;
  images: ImageItem[];
  initialIndex?: number;
  onClose: () => void;
}

const THUMB_HEIGHT = 64;
const THUMB_AREA_HEIGHT = THUMB_HEIGHT + 24;

export default function ImageCarousel({
  open,
  images,
  initialIndex = 0,
  onClose,
}: Props) {
  const safeInitialIndex =
    images.length > 0
      ? Math.min(Math.max(initialIndex, 0), images.length - 1)
      : 0;

  const [index, setIndex] = useState(safeInitialIndex);

  useEffect(() => {
    if (!open) return;

    const nextIndex =
      images.length > 0
        ? Math.min(Math.max(initialIndex, 0), images.length - 1)
        : 0;

    setIndex(nextIndex);
  }, [open, initialIndex, images.length]);

  useEffect(() => {
    if (index > images.length - 1) {
      setIndex(Math.max(images.length - 1, 0));
    }
  }, [images.length, index]);

  const handleChangeIndex = useCallback(
    (nextIndex: number) => {
      const clamped = Math.min(Math.max(nextIndex, 0), images.length - 1);
      setIndex(clamped);
    },
    [images.length]
  );

  const handlePrev = useCallback(() => {
    setIndex((i) => Math.max(i - 1, 0));
  }, []);

  const handleNext = useCallback(() => {
    setIndex((i) => Math.min(i + 1, images.length - 1));
  }, [images.length]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (index < images.length - 1) handleNext();
    },
    onSwipedRight: () => {
      if (index > 0) handlePrev();
    },
    trackMouse: true,
    preventScrollOnSwipe: true,
  });

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogContent
        sx={{
          p: 0,
          bgcolor: "black",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 10,
            color: colors.white,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            position: "absolute",
            inset: 0,
            px: 1,
            pointerEvents: "none",
            zIndex: 5,
          }}
        >
          <IconButton
            onClick={handlePrev}
            disabled={index === 0}
            sx={{ color: colors.white, pointerEvents: "auto" }}
          >
            <ChevronLeftIcon fontSize="large" />
          </IconButton>

          <IconButton
            onClick={handleNext}
            disabled={index >= images.length - 1}
            sx={{ color: colors.white, pointerEvents: "auto" }}
          >
            <ChevronRightIcon fontSize="large" />
          </IconButton>
        </Stack>

        <Box
          {...swipeHandlers}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            touchAction: "pan-y",
          }}
        >
          <Box
            sx={{
              display: "flex",
              height: "100%",
              width: `${images.length * 100}%`,
              transform: `translateX(-${index * (100 / images.length)}%)`,
              transition: "transform 0.3s ease",
            }}
          >
            {images.map((img, i) => (
              <Box
                key={i}
                sx={{
                  width: `${100 / images.length}%`,
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={images[index].uri || images[index].urls.href_original}
                  alt={images[index].image_name}
                  sx={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    objectFit: "contain",
                    userSelect: "none",
                    WebkitUserDrag: "none",
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            height: THUMB_AREA_HEIGHT,
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            borderTop: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              maxWidth: "100%",
              overflowX: "auto",
            }}
          >
            {images.map((img, i) => (
              <Box
                key={`thumb-${i}`}
                component="img"
                src={img.uri || img.urls.href_small}
                alt={img.image_name}
                onClick={() => handleChangeIndex(i)}
                sx={{
                  height: THUMB_HEIGHT,
                  width: THUMB_HEIGHT,
                  cursor: "pointer",
                  borderRadius: 1,
                  opacity: i === index ? 1 : 0.5,
                  border:
                    i === index
                      ? `2px solid ${colors.white}`
                      : "2px solid transparent",
                  transition: "opacity 0.2s, border 0.2s",
                  "&:hover": {
                    opacity: 1,
                  },
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
            ))}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}